import { Switch } from '@/components/ui/switch'
import { useCalculateResults } from '@/hooks/useCalculations'
import { useCalculatorStore } from '@/store/calculatorStore'
import { useEffect, useState } from 'react'
import { ChevronRight as IconChevronRight, Info } from 'lucide-react'

const CalculatorSummary = ({ type }: { type: 'improved' | 'unimproved' }) => {
  const [view, setView] = useState<
    'improved' | 'unimproved' | 'tjueTreFemUnimproved' | 'tjueTreFemImproved'
  >(type)

  const show235 = useCalculatorStore((state) => state.show235)
  const setShow235 = useCalculatorStore((state) => state.setShow235)
  const studyStartYear = useCalculatorStore(
    (state) => state.userDetails.studyStartYear
  )

  useEffect(() => {
    if (show235) {
      if (type === 'improved') {
        setView('tjueTreFemImproved')
      } else {
        setView('tjueTreFemUnimproved')
      }
    } else {
      setView(type)
    }
  }, [type, show235])

  function handle235Change() {
    if (show235) {
      setShow235(false)
      setView(type)
    } else {
      setShow235(true)
      if (type === 'improved') {
        setView('tjueTreFemImproved')
      } else {
        setView('tjueTreFemUnimproved')
      }
    }
  }

  const results = useCalculateResults()
  const isImproved =
    results[view].total_normal > results['unimproved'].total_normal

  return (
    <div
      className="px-7 pt-4 min-[1100px]:pt-0"
      data-testid={`calculator-summary-${type}`}
    >
      <div className="flex justify-center min-[1100px]:justify-start gap-2 items-center">
        {(view === 'improved' || view === 'tjueTreFemImproved') && (
          <>
            <h2
              className="text-4xl min-[1100px]:text-5xl font-bold text-purple-800"
              data-testid="original-score"
            >
              {results['unimproved'].total_normal}
            </h2>
            <div className="flex items-center">
              <IconChevronRight
                strokeWidth={1.5}
                className="size-6 text-warm-gray-600 min-[1100px]:size-8"
                data-testid="score-improvement-arrow"
              />
            </div>
          </>
        )}
        <h2
          className={`text-4xl min-[1100px]:text-5xl font-bold ${
            type === 'improved'
              ? isImproved
                ? 'text-green-800'
                : 'text-red-800'
              : 'text-purple-800'
          }`}
          data-testid="current-score"
        >
          {results[view].total_normal}
        </h2>
      </div>
      <p className="text-lg font-bold text-center min-[1100px]:text-left min-[1100px]:mt-2 min-[1100px]:text-xl">
        Konkurransepoeng
      </p>
      <div className="flex items-center gap-2 mt-3 mb-4">
        <Switch
          checked={show235}
          onCheckedChange={handle235Change}
          data-testid="show-235-switch"
        />
        <span className="text-sm">Vis med 23/5-regel</span>
      </div>
      <table className="w-full text-sm" data-testid="score-breakdown-table">
        <tbody>
          <tr className="[&>td]:py-1">
            <td>Karakterpoeng</td>
            <td className="text-right" data-testid="grade-points">
              <strong>{results[view].grades} poeng</strong>
            </td>
          </tr>
          <tr className="[&>td]:py-1">
            <td>+ Språk- og realfagspoeng</td>
            <td className="text-right" data-testid="science-language-points">
              <strong>{results[view].totalScienceAndLanguage} poeng</strong>
            </td>
          </tr>
          {results[view].language === 1.5 && (
            <tr>
              <td colSpan={2} className="py-2">
                <div className="flex gap-2 items-center px-2 py-2 bg-yellow-100 border border-yellow-300 rounded-md text-xs text-gray-500">
                  <Info className="size-4 flex-shrink-0" />
                  <span>
                    Har du tatt flere fremmedspråk eller har samme fremmedspråk
                    på flere nivåer, må du kontakte studierådgiver for riktig
                    utregning av språkpoeng
                  </span>
                </div>
              </td>
            </tr>
          )}
          <tr className="[&>td]:py-1">
            <td className={studyStartYear >= 2028 ? 'text-gray-300' : ''}>
              + Alderspoeng
            </td>
            <td
              className={`text-right ${studyStartYear >= 2028 ? 'text-gray-300' : ''}`}
              data-testid="age-points"
            >
              <strong>{results[view].age_normal} poeng</strong>
            </td>
          </tr>
          <tr className="[&>td]:py-1">
            <td>+ Tilleggspoeng</td>
            <td className="text-right" data-testid="extra-points">
              <strong>{results[view].extra} poeng</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default CalculatorSummary
